// import

import type {CtaNode} from '@arc/hub-model';
import type {ComponentProps} from 'react';

import {styled} from '@linaria/react';
import {darken} from 'polished';

import {cram} from '@arc/rambo';

import {CtaLinkButton, SignupForm, useSignupForm} from 'modules/SignupForm';
import {useTheme} from 'modules/Theme';
import {Type} from 'modules/Type';

import {Book} from './Book';
import {buttonColors} from './SignupHeader';

// types

type Props = ComponentProps<typeof Footer> & {
  cta: CtaNode;
};

// component

export function SignupFooter(props: Props) {
  const {cta: {body, meta}, ...rest} = props;
  const {media: {coverImage, vectorImage, alt}, headline, themeColor, buttonText, buttonLink, mailerliteGroups} = meta;
  const formProps = useSignupForm(mailerliteGroups);
  const {palette} = useTheme();
  const colorPick = palette[themeColor][95];

  return (
    <Footer
      {...rest}
      data-test="blog-footer"
      style={{
        '--c0': colorPick,
        '--c1': darken(0.1, colorPick),
        '--c2': darken(0.25, colorPick),
        '--c3': darken(0.4, colorPick),
      }}>
      <div className="content">
        <div className="inner">
          <div className="media">
            {coverImage ? (
              <Book
                coverColor={palette[themeColor][50]}
                coverImage={coverImage}
                title={alt ?? headline} />
            ) : vectorImage ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={alt ?? headline}
                className="image"
                src={vectorImage} />
            ) : null}
          </div>

          <div className="copy">
            <Type className="title">{headline}</Type>

            <Type className="blurb">
              {body.trim() ? (
                <div dangerouslySetInnerHTML={{__html: body}} />
              ) : null}

              {buttonLink ? (
                <CtaLinkButton
                  className={cram('button', buttonColors[themeColor])}
                  href={buttonLink}>
                  {buttonText}
                </CtaLinkButton>
              ) : (
                <SignupForm
                  {...formProps}
                  buttonClass={cram('button', buttonColors[themeColor])}
                  buttonText={buttonText}
                  className="form" />
              )}
            </Type>
          </div>
        </div>
      </div>
    </Footer>
  );
}

// styles

const Footer = styled('aside')`
margin: 1.5rem;
filter:
  drop-shadow(0.75rem 0.75rem 0 var(--c1))
  drop-shadow(-0.75rem -0.75rem 0 var(--c1))
  drop-shadow(-0.5rem 0.5rem 0 var(--c2))
  drop-shadow(0.5rem -0.5rem 0 var(--c2))
  drop-shadow(0.25rem 0 0 var(--c3))
  drop-shadow(-0.25rem 0 0 var(--c3))
  drop-shadow(0 0.25rem 0 var(--c3))
  drop-shadow(0 -0.25rem 0 var(--c3));

.content {
  padding: 1.5rem;
  position: relative;
  background-color: var(--c0);
}

.inner {
  margin: 0 auto;
  max-width: var(--bp-sm);
  display: flex;
  gap: 1.5rem;
}

.media {
  flex: 0 1 clamp(6rem, 25%, 12rem);
  max-height: 24rem;
}

.image {
  display: block;
  width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.copy {
  flex: 0 1 75%;
  align-self: center;
}

.title {
  font-family: var(--font-head);
  font-size: 2.25rem;
  line-height: 2.75rem;
  letter-spacing: 0;
  font-weight: 500;
  margin: 0 0 0.5rem;

  strong {
    font-weight: 700;
    font-variation-settings: initial;
  }
}

.blurb {
  line-height: 1.75rem;

  div > * { margin-bottom: 1rem; }
}

.form {
  display: grid;
  gap: 0.5rem;
  align-items: start;
  grid-template-columns: minmax(auto, 65%) auto;

  input[type="email"] { margin-bottom: 0; }

  button {
    height: 2.5rem;
    line-height: 2.5rem;
    padding: 0 1rem;
    white-space: nowrap;
  }
}

@media (max-width: 35.99875em) {
  .inner {
    flex-flow: column;
  }

  .media {
    align-self: center;
    width: clamp(6rem, 50%, 12rem);
  }

  .form {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
}
`;
